import React from "react"
import { StaticQuery, graphql } from "gatsby"
import { ProgramOverviewWrapperBlokProps } from "../s22_overview-program"
import Select from "../input-v2/Select"
import { ProgramCardCmp } from "../s22_program-card/ProgramCard"
import IconV2 from "../icon-v2"
import ButtonV2 from "../button-v2"

import {
  StyledProgramOverview,
  StyledProgramOverviewToolbar,
  StyledProgramOverviewFiltersContent,
  StyledProgramOverviewInput,
  StyledProgramOverviewResults,
  StyledProgramOverviewAlert,
  StyledProgramOverviewMobileTools,
  StyledProgramOverviewFilterToggle,
  StyledProgramOverviewFilters,
  StyledProgramOverviewFiltersHeader,
  StyledProgramOverviewFiltersHeaderBack,
  StyledProgramOverviewAction,
} from "./partials"

export interface ProgramOverviewProps {
  degree: Array<string>
  location: Array<string>
  specialization: Array<string>
  hide_filters: boolean
  hide_degree_filter: boolean
  hide_location_filter: boolean
  hide_specialization_filter: boolean
  cta_option: string
}

declare global {
  interface Array<T> {
    intersects(list: Array<T>): Array<T>
    isFilter(list: Array<T>): boolean
  }
}

Array.prototype.intersects = function (list: any[]): any[] {
  return this?.filter((i: any) => list.includes(i))
}

Array.prototype.isFilter = function (list: any[]): boolean {
  return this.length === 0 || this.intersects(list).length > 0
}

const S22ProgramOverview: React.FC<ProgramOverviewProps> = ({
  degree,
  location,
  specialization,
  hide_filters,
  hide_degree_filter,
  hide_location_filter,
  hide_specialization_filter,
  cta_option,
}) => {
  function parseQueryResult(
    data: any,
    degreeFilter: Array<string>,
    locationFilter: Array<string>,
    specializationFilter: Array<string>
  ): Array<ProgramOverviewWrapperBlokProps> {
    const parsedResult: Array<ProgramOverviewWrapperBlokProps> = []
    data.allStoryblokEntry.edges.forEach((edge: any) => {
      const { node } = edge
      let content: any = node.content
      if (typeof node.content === "string") content = JSON.parse(node.content)
      if (
        locationFilter.isFilter(content.location) &&
        specializationFilter.isFilter(content.specialization) &&
        degreeFilter.isFilter(content.degree)
      ) {
        parsedResult.push({ blok: content } as ProgramOverviewWrapperBlokProps)
      }
    })
    return parsedResult
  }

  const [selectedLocation, setSelectedLocation] = React.useState<string[]>(location)
  const [selectedDegree, setSelectedDegree] = React.useState<string[]>(degree)
  const [selectedSpecialization, setSelectedSpecialization] = React.useState<string[]>(specialization)
  const [showFilters, setShowFilters] = React.useState<boolean>(false)

  return (
    <StaticQuery
      query={graphql`
        query {
          allStoryblokDatasourceEntry(
            filter: {
              data_source: {
                in: ["specialization", "program-categories", "location"]
              }
            }
          ) {
            nodes {
              data_source
              name
              value
            }
          }
          allStoryblokEntry(
            filter: { field_component: { eq: "s22_program-card" } }
            sort: { fields: [field_type_string, slug], order: [ASC, ASC] }
          ) {
            edges {
              node {
                id
                slug
                full_slug
                content
                position
                field_type_string
              }
            }
          }
        }
      `}
      render={(data) => {
        const sBdataSource = data.allStoryblokDatasourceEntry.nodes
        let stories = parseQueryResult(
          data,
          selectedDegree,
          selectedLocation,
          selectedSpecialization
        )

        return (
          <StyledProgramOverview>
            <StyledProgramOverviewToolbar hide_filters={hide_filters}>
              <StyledProgramOverviewMobileTools>
                <StyledProgramOverviewFilterToggle onClick={() => setShowFilters(!showFilters)}>
                  <p>Filter Courses</p>
                  <IconV2 size="16" icon="filter" />
                </StyledProgramOverviewFilterToggle>
              </StyledProgramOverviewMobileTools>
              <StyledProgramOverviewFilters visible={showFilters}>
                <StyledProgramOverviewFiltersHeader>
                  <StyledProgramOverviewFiltersHeaderBack onClick={() => setShowFilters(!showFilters)}>
                    <IconV2 size="16" icon="arrow-left" />
                  </StyledProgramOverviewFiltersHeaderBack>
                  <p>Filter Courses</p>
                </StyledProgramOverviewFiltersHeader>
                <StyledProgramOverviewFiltersContent>
                  {!hide_degree_filter && (
                    <StyledProgramOverviewInput>
                      <Select
                        onChange={(event) => {
                          if (event.target.value === "all")
                            setSelectedDegree(degree)
                          else setSelectedDegree([event.target.value])
                        }}
                        label="Degree"
                        isDark={true}
                        defaultValue="all"
                      >
                        <option value="all">All Degrees</option>
                        {sBdataSource
                          .filter(
                            (entry) =>
                              entry.data_source === "program-categories"
                          )
                          .map(
                            (option) =>
                              (degree.length === 0 ||
                                degree.includes(option.value)) && (
                                <option key={option.value} value={option.value}>
                                  {option.name}
                                </option>
                              )
                          )}
                      </Select>
                    </StyledProgramOverviewInput>
                  )}
                  {!hide_location_filter && (
                    <StyledProgramOverviewInput>
                      <Select
                        onChange={(event) => {
                          if (event.target.value === "all")
                            setSelectedLocation(location)
                          else setSelectedLocation([event.target.value])
                        }}
                        label="Location"
                        isDark={true}
                        defaultValue="all"
                      >
                        <option value="all">All Locations</option>
                        {sBdataSource
                          .filter((entry) => entry.data_source === "location")
                          .map(
                            (option) =>
                              (degree.length === 0 ||
                                location.includes(option.value)) && (
                                <option key={option.value} value={option.value}>
                                  {option.name}
                                </option>
                              )
                          )}
                      </Select>
                    </StyledProgramOverviewInput>
                  )}
                  {!hide_specialization_filter && (
                    <StyledProgramOverviewInput>
                      <Select
                        onChange={(event) => {
                          if (event.target.value === "all")
                            setSelectedSpecialization(specialization)
                          else setSelectedSpecialization([event.target.value])
                        }}
                        label="Specialization"
                        isDark={true}
                        defaultValue="all"
                      >
                        <option value="all">All Specializations</option>
                        {sBdataSource
                          .filter(
                            (entry) => entry.data_source === "specialization"
                          )
                          .map(
                            (option) =>
                              (degree.length === 0 ||
                                specialization.includes(option.value)) && (
                                <option key={option.value} value={option.value}>
                                  {option.name}
                                </option>
                              )
                          )}
                      </Select>
                    </StyledProgramOverviewInput>
                  )}
                  <StyledProgramOverviewAction mobile_only={true}>
                    <ButtonV2
                      className="button-v2--primary"
                      label="Show Results"
                      onClick={() => setShowFilters(!showFilters)}
                    />
                  </StyledProgramOverviewAction>
                </StyledProgramOverviewFiltersContent>
              </StyledProgramOverviewFilters>
            </StyledProgramOverviewToolbar>

            <StyledProgramOverviewResults no_results={stories.length === 0}>
              {stories && stories.length > 0 ? (
                <>
                  {stories.map((s) => (
                    (s.blok.specialization[0] === selectedSpecialization[0] || s.blok.initial_hide === false) && 
                    <ProgramCardCmp
                      title={s.blok.title}
                      description={s.blok.description}
                      duration={s.blok.duration}
                      timeline={s.blok.timeline}
                      buttons={s.blok.buttons}
                      degree={s.blok.degree}
                      header_image={s.blok.header_image}
                      location={s.blok.location}
                      specialization={s.blok.specialization}
                      initial_hide={s.blok.initial_hide}
                      cta_web={cta_option === "cta-web" ? s.blok.cta_web : false}
                      cta_lp1={cta_option === "cta-lp1" ? s.blok.cta_lp1 : false}
                      cta_lp2={cta_option === "cta-lp2" ? s.blok.cta_lp2 : false}
                      cta_lp3={cta_option === "cta-lp3" ? s.blok.cta_lp3 : false}
                    ></ProgramCardCmp>
                  ))}
                </>
              ) : (
                <StyledProgramOverviewAlert>
                  No results found.
                </StyledProgramOverviewAlert>
              )}
            </StyledProgramOverviewResults>
          </StyledProgramOverview>
        )
      }}
    />
  )
}

export default S22ProgramOverview
